/**
 * Parsing helpers for the timeline's TimeDisplayField input.
 *
 * Each lower-level parser returns a valid result or null for unparseable
 * input. `parseToSeekTarget` dispatches to the right parser for the active
 * TimeDisplayMode and normalizes the result into a SeekTarget.
 */

import { TimeDisplayMode } from "@/shared/components/visualization/WorkspaceCtx";
import { nanoSecToMilliSecLossy, NS_PER_MS_BIGINT } from "@/shared/time";

export type SeekTarget = { elapsedMs: number } | { absoluteNs: bigint };

export interface SeekBounds {
  earliestTimeNs: bigint | null;
  latestTimeNs: bigint | null;
  durationMs: number;
}

/**
 * Returns true if the parsed target falls within the timeline's outer bounds.
 *
 * Elapsed targets are checked against [0, durationMs]. Absolute targets are
 * checked against [earliestTimeNs, latestTimeNs]; if either bound is null,
 * absolute targets are accepted (we cannot reject what we cannot bound).
 *
 * Gap-aware validation (rejecting absolute timestamps that fall between
 * adjacent TimeSpans) is out of scope — the Timer's seekTo is a no-op for
 * in-gap values, so they degrade gracefully.
 */
export function isSeekTargetInBounds(
  target: SeekTarget,
  bounds: SeekBounds,
): boolean {
  if ("elapsedMs" in target) {
    return target.elapsedMs >= 0 && target.elapsedMs <= bounds.durationMs;
  }
  if (bounds.earliestTimeNs === null || bounds.latestTimeNs === null) {
    return true;
  }
  return (
    target.absoluteNs >= bounds.earliestTimeNs &&
    target.absoluteNs <= bounds.latestTimeNs
  );
}

/**
 * Shared body for the two seconds-with-decimal-fraction parsers below.
 * Accepts non-negative digit strings with an optional `.fraction` suffix,
 * right-pads the fraction to 9 digits, and returns the value in nanoseconds.
 * A leading dot (".5") is treated as an empty whole part equal to zero.
 *
 * Returns null for empty, non-numeric, or negative input.
 */
function parseDecimalFractionalSeconds(input: string): bigint | null {
  if (input === "" || input.startsWith("-")) {
    return null;
  }

  const dotIndex = input.indexOf(".");
  let wholePart: string;
  let fracPart: string;
  if (dotIndex === -1) {
    wholePart = input;
    fracPart = "0";
  } else {
    wholePart = input.slice(0, dotIndex);
    fracPart = input.slice(dotIndex + 1);
  }

  if (wholePart === "") {
    wholePart = "0";
  }

  if (!/^\d+$/.test(wholePart) || !/^\d+$/.test(fracPart)) {
    return null;
  }

  const normalized = fracPart.slice(0, 9).padEnd(9, "0");
  return BigInt(wholePart) * 1_000_000_000n + BigInt(normalized);
}

/**
 * Parses an elapsed-seconds string (e.g. "125.123456789") into nanoseconds.
 * The fractional part is right-padded to 9 digits; sub-nanosecond digits are
 * truncated. Returns null for empty, non-numeric, or negative input.
 */
export function parseElapsedSecondsToNs(input: string): bigint | null {
  return parseDecimalFractionalSeconds(input);
}

/**
 * Parses an elapsed-duration string (e.g. "00:02:05.340") into milliseconds.
 *
 * The regex permits hours of any digit length and minutes/seconds of 1-2
 * digits; the digit-only pattern rejects sign characters. Minutes and
 * seconds above 59 are rejected. Sub-millisecond fractional digits are
 * truncated (not rounded), so "00:00:00.999999999" returns 999, never 1000
 * — the return type is integer milliseconds and rounding up could carry
 * into the seconds field, breaking callers reasoning about bounds.
 *
 * Returns null for invalid format or out-of-range minutes/seconds.
 */
export function parseElapsedDuration(input: string): number | null {
  if (input === "") {
    return null;
  }

  const match = /^(\d+):(\d{1,2}):(\d{1,2})(?:\.(\d+))?$/.exec(input);
  if (match === null) {
    return null;
  }

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const seconds = parseInt(match[3], 10);
  const fracStr = match[4] ?? "";

  if (minutes > 59 || seconds > 59) {
    return null;
  }

  // Truncate to ms precision via integer math; floating-point on long
  // fractional strings rounds 0.999999999 up to 1.0.
  const fracMs = parseInt(fracStr.slice(0, 3).padEnd(3, "0"), 10);
  return hours * 3600000 + minutes * 60000 + seconds * 1000 + fracMs;
}

/**
 * Parses a log-epoch string (e.g. "1710524445.123456789") into absolute
 * nanoseconds. Shares parsing semantics with `parseElapsedSecondsToNs`;
 * both exist as separate exports so call sites communicate intent (elapsed
 * duration vs absolute log epoch).
 *
 * Returns null for empty, non-numeric, or negative input.
 */
export function parseLogEpoch(input: string): bigint | null {
  return parseDecimalFractionalSeconds(input);
}

/**
 * Parses the en-US 12-hour format produced by `dateToLocalTimestamp`
 * (e.g. "2025-12-02 12:04:08.247 PM PST") into nanoseconds since epoch.
 *
 * The trailing timezone abbreviation is informational — the JS Date is
 * constructed in the user's local timezone, the same one the UI used to
 * format the displayed value, so the string round-trips exactly. The TZ
 * suffix is optional to tolerate copy/paste trimming.
 *
 * Returns null on parse failure. Pinning to this exact format (rather than
 * leaning on Date.parse) keeps Local-mode round-tripping deterministic
 * across browsers — Date.parse is implementation-defined outside simplified
 * ISO 8601, and Safari has historically rejected the en-US AM/PM shape
 * Chrome accepts.
 *
 * Day-31-in-February passes the digit bounds check and silently rolls over
 * via `new Date()`. Acceptable: the parser exists to round-trip the UI's
 * own output, not to validate arbitrary user-typed dates.
 */
export function parseLocalDisplay(input: string): bigint | null {
  const match =
    /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})\.(\d{3}) (AM|PM)(?: .+)?$/.exec(
      input,
    );
  if (match === null) {
    return null;
  }

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  let hour = parseInt(match[4], 10);
  const minute = parseInt(match[5], 10);
  const second = parseInt(match[6], 10);
  const ms = parseInt(match[7], 10);
  const ampm = match[8];

  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour < 1 ||
    hour > 12 ||
    minute > 59 ||
    second > 59
  ) {
    return null;
  }

  // 12-hour → 24-hour: 12 AM = 0, 12 PM = 12, others shift by 12 for PM.
  if (ampm === "PM" && hour !== 12) {
    hour += 12;
  } else if (ampm === "AM" && hour === 12) {
    hour = 0;
  }

  const date = new Date(year, month - 1, day, hour, minute, second, ms);
  return BigInt(date.getTime()) * NS_PER_MS_BIGINT;
}

/**
 * Parses a UTC ISO 8601 string into nanoseconds since epoch. Mirrors the
 * shape produced by `nanoSecToUTCTimestamp`
 * ("YYYY-MM-DDTHH:MM:SS.fffffffffZ") and tolerates 0-9 fractional digits
 * plus an optional trailing `Z` to handle hand-typed or trimmed input.
 *
 * Bypasses `Date.parse` because ECMA-262's simplified ISO profile specifies
 * exactly 3 fractional digits; longer fractions are implementation-defined.
 * V8 truncates them, but JavaScriptCore (Safari) has historically returned
 * NaN, which would break round-tripping for the formatter's 9-digit output.
 * A bespoke regex keeps parsing deterministic across browsers and preserves
 * full ns precision (where `Date.parse` would cap at ms).
 *
 * Returns null on parse failure or out-of-range fields.
 */
export function parseUTCDisplay(input: string): bigint | null {
  const match =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?Z?$/.exec(
      input,
    );
  if (match === null) {
    return null;
  }

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  const hour = parseInt(match[4], 10);
  const minute = parseInt(match[5], 10);
  const second = parseInt(match[6], 10);
  const fracStr = match[7] ?? "";

  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    return null;
  }

  // Date.UTC treats its arguments as UTC fields (unlike `new Date(...)`,
  // which uses the runner's local timezone).
  const ms = Date.UTC(year, month - 1, day, hour, minute, second);
  if (Number.isNaN(ms)) {
    return null;
  }

  const fracNs = BigInt(fracStr.padEnd(9, "0"));
  return BigInt(ms) * NS_PER_MS_BIGINT + fracNs;
}

/**
 * Parses a raw string into a SeekTarget for the active TimeDisplayMode.
 * Returns null when the input is empty or unparseable.
 *
 * Each mode parses only the format it emits: dedicated regex parsers for
 * Elapsed/ElapsedTime/Log, `parseLocalDisplay` for the en-US 12-hour shape
 * pinned by `dateToLocalTimestamp`, and `parseUTCDisplay` for the
 * ns-precision ISO 8601 shape produced by `nanoSecToUTCTimestamp`.
 */
export function parseToSeekTarget(
  input: string,
  mode: TimeDisplayMode,
): SeekTarget | null {
  const trimmed = input.trim();
  if (trimmed === "") {
    return null;
  }

  switch (mode) {
    case TimeDisplayMode.Elapsed: {
      const ns = parseElapsedSecondsToNs(trimmed);
      // nanoSecToMilliSecLossy preserves the integer-millisecond portion
      // when ns exceeds Number.MAX_SAFE_INTEGER (about 104 days), unlike
      // Number(ns) / NS_PER_MS. Sub-millisecond precision is dropped because
      // elapsedMs is a number; this confines the precision loss to that.
      return ns !== null ? { elapsedMs: nanoSecToMilliSecLossy(ns) } : null;
    }
    case TimeDisplayMode.ElapsedTime: {
      const ms = parseElapsedDuration(trimmed);
      return ms !== null ? { elapsedMs: ms } : null;
    }
    case TimeDisplayMode.Log: {
      const ns = parseLogEpoch(trimmed);
      return ns !== null ? { absoluteNs: ns } : null;
    }
    case TimeDisplayMode.Local: {
      const ns = parseLocalDisplay(trimmed);
      return ns !== null ? { absoluteNs: ns } : null;
    }
    case TimeDisplayMode.UTC: {
      const ns = parseUTCDisplay(trimmed);
      return ns !== null ? { absoluteNs: ns } : null;
    }
  }
}
